"use client";
import type { RowData } from "@tanstack/react-table";

import { Card } from "@/components/ui/card";
import { useContextualNavigationBoundary } from "@/components/navigation/use-contextual-navigation-boundary";
import { cn } from "@/lib/utils";
import { RecordGrid } from "./grid/record-grid";
import { RecordPagination } from "./record-pagination";
import { emptyCopy } from "./record-table-support";
import { RecordTableToolbar } from "./record-table-toolbar";
import type { RecordTableModel } from "./use-record-table-model";
import type { RecordTableProps } from "./types";

const RECORD_CARD_BASE = "flex min-h-0 flex-1 flex-col gap-0 overflow-hidden p-0";
const RECORD_CARD_PHONE_BLEED = "max-md:-mx-(--page-gutter-x) max-md:rounded-none max-md:border-x-0 max-md:shadow-none";

export const RECORD_CARD_CLASS = `${RECORD_CARD_BASE} ${RECORD_CARD_PHONE_BLEED}`;

export const RECORD_DENSITY: Record<`--${string}`, string> = {
  "--table-row-height": "40px",
  "--table-header-height": "36px",
  "--table-cell-px": "12px",
  "--table-cell-py": "6px",
  "--table-header-py": "6px",
  "--table-font-size": "13px",
};

interface RecordTableBodyProps<TData extends RowData> {
  props: RecordTableProps<TData>;
  model: RecordTableModel<TData>;
  onOpenColumns: () => void;
  onSaveView: () => void;
}

function RecordTableGrid<TData extends RowData>({
  props,
  model,
}: Pick<RecordTableBodyProps<TData>, "props" | "model">) {
  const { object, state, data } = props;
  return (
    <RecordGrid
      object={object}
      state={state}
      data={{ ...data, rows: model.rows }}
      selectable={model.selectable}
      rowActions={props.rowActions}
      onRowClick={props.onRowClick}
      onRowHover={props.onRowHover}
      activeRowId={props.activeRowId}
      getRowClassName={props.getRowClassName}
      renderExpandedRow={props.renderExpandedRow}
      renderMobileRow={props.renderMobileRow}
      infiniteScroll={model.infiniteScroll}
      canLoadMore={model.canLoadMore}
      onLoadMore={model.onLoadMore}
      {...emptyCopy(object.label, model.hasFilters, props)}
      emptyAction={props.emptyAction}
    />
  );
}

export function RecordTableBody<TData extends RowData>(
  bodyProps: RecordTableBodyProps<TData>,
) {
  const { props, model } = bodyProps;
  const { state } = props;
  const navigationBoundaryProps = useContextualNavigationBoundary();
  const isTable = model.presentation.mode === "table";
  return (
    <Card
      {...navigationBoundaryProps}
      style={RECORD_DENSITY}
      className={cn(
        props.contained ? RECORD_CARD_BASE : RECORD_CARD_CLASS,
        props.embedded && "rounded-none border-0",
      )}
    >
      {props.banner}
      <RecordTableToolbar {...bodyProps} />
      {props.subToolbar && isTable ? props.subToolbar : null}
      {isTable ? (
        <RecordTableGrid props={props} model={model} />
      ) : (
        model.presentationContent
      )}
      {model.shouldShowPagination ? (
        <RecordPagination
          page={model.page}
          pageSize={state.pageSize}
          total={model.total}
          rowCount={model.paginationRowCount}
          canLoadMore={model.canLoadMore}
          onPageChange={state.setPage}
          onPageSizeChange={state.setPageSize}
        />
      ) : null}
    </Card>
  );
}
